import { Capacitor } from "@capacitor/core";
import { useCallback, useState } from "react";
import { WebQRScanner } from "../web-qr-scanner";
import { useNativeQRScanner } from "./use-native-qr-scanner";

interface UseQRScannerOptions {
  onScanSuccess: (data: string) => void | Promise<void>;
  onError?: (error: Error) => void;
}

export function useQRScanner({ onScanSuccess, onError }: UseQRScannerOptions) {
  const [isWebScannerOpen, setIsWebScannerOpen] = useState(false);
  const isWeb = Capacitor.getPlatform() === "web";

  // Use native scanner for mobile platforms
  const nativeScanner = useNativeQRScanner({
    onScanSuccess,
    onError,
  });

  const scanQRCode = useCallback(async () => {
    if (isWeb) {
      // For web, open the modal scanner
      setIsWebScannerOpen(true);
    } else {
      // For native platforms, use the native scanner
      await nativeScanner.scanQRCode();
    }
  }, [isWeb, nativeScanner]);

  const handleWebScanSuccess = useCallback(
    async (data: string) => {
      setIsWebScannerOpen(false);
      await onScanSuccess(data);
    },
    [onScanSuccess],
  );

  const handleWebScanClose = useCallback(() => {
    setIsWebScannerOpen(false);
  }, []);

  // Return the scanner component for web platforms
  const WebScannerComponent = isWeb
    ? () => (
        <WebQRScanner
          isOpen={isWebScannerOpen}
          onClose={handleWebScanClose}
          onScanSuccess={handleWebScanSuccess}
        />
      )
    : null;

  return {
    scanQRCode,
    isScanning: isWeb ? isWebScannerOpen : nativeScanner.isScanning,
    WebScannerComponent,
  };
}
